import React, { useState, useRef, useEffect } from 'react';

const SelectedInput = ({
  id,
  label,
  value,
  onChange,
  options,
  className,
  onBlur,
  showError,
  showSuccess,
  errorMessage,
  searchable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Determine the CSS class based on validation state
  const getSelectClass = () => {
    if (showError) return 'confluent-form-input error-field';
    if (showSuccess) return 'confluent-form-input success-field';
    return className || 'confluent-form-input';
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search text
  const filteredOptions = searchable && searchText
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      )
    : options;

  // Get the label for the selected value
  const getSelectedLabel = () => {
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption ? selectedOption.label : '';
  };

  // Handle option selection
  const handleOptionClick = (optionValue) => {
    const syntheticEvent = {
      target: {
        name: id,
        value: optionValue
      }
    };
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchText('');
    if (onBlur) {
      onBlur();
    }
  };

  // If not searchable, render regular select
  if (!searchable) {
    return (
      <div className="input-wrapper">
        <label htmlFor={id} className="confluent-form-label">{label}</label>
        <select
          id={id}
          name={id}
          className={getSelectClass()}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        >
          {options.map((option) => (
            <option key={option.value} disabled={option.disabled} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {showError && errorMessage && (
          <div className="field-error-message">{errorMessage}</div>
        )}
        {showSuccess && (
          <div className="field-success-message">✓</div>
        )}
      </div>
    );
  }

  // Render searchable select
  return (
    <div className="input-wrapper" ref={dropdownRef}>
      <label htmlFor={id} className="confluent-form-label">{label}</label>
      <div className="searchable-select">
        <div
          className={`searchable-select-trigger ${getSelectClass()} ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={value ? '' : 'placeholder'}>
            {value ? getSelectedLabel() : options.find(o => o.disabled)?.label || 'Select...'}
          </span>
          <svg
            className={`select-chevron ${isOpen ? 'open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M2 4L6 8L10 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {isOpen && (
          <div className="searchable-select-dropdown">
            <div className="searchable-select-search">
              <input
                ref={searchInputRef}
                type="text"
                className="searchable-select-search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="searchable-select-options">
              {filteredOptions.length === 0 ? (
                <div className="searchable-select-option disabled">No options found</div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`searchable-select-option ${
                      option.value === value ? 'selected' : ''
                    } ${option.disabled ? 'disabled' : ''}`}
                    onClick={() => !option.disabled && handleOptionClick(option.value)}
                  >
                    {option.label}
                    {option.value === value && (
                      <svg className="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M3 8L6.5 11.5L13 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {showError && errorMessage && (
        <div className="field-error-message">{errorMessage}</div>
      )}
      {showSuccess && (
        <div className="field-success-message">✓</div>
      )}
    </div>
  );
};

export default SelectedInput;
